import React, { useState, useEffect, useCallback } from "react";
import "./Pages.css";
import { useHistory } from "react-router-dom";
import { Button, CircularProgress, LinearProgress, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import PanoramaOutlinedIcon from "@mui/icons-material/PanoramaOutlined";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import useBusinessQuery from "../../store/Bussiness/useBusinessQuery";
import useImageQuery from "../../store/Images/useImageQuerys";
import ImageRemovalModal from "../myListing/Listing/Add business/ImageRemovalModal";
import { uploadFileToS3 } from "../../utils/s3Upload";

// ── Image limits (configure here) ──
const LIMITS = { shop: 20, banner: 10, cover: 20, catalogue: 50 };

let _opId = 0;
const newOpId = () => `op_${++_opId}`;

const EditImages = () => {
  const history = useHistory();

  // ── Server state ──
  const [businessId, setBusinessId] = useState(null);
  const [serverShop, setServerShop] = useState([]);     // [{ id: "shop", url }]
  const [serverBanner, setServerBanner] = useState([]); // [{ id: "banner", url }]
  const [serverCover, setServerCover] = useState([]);   // [{ id, cover_image }]
  const [serverCat, setServerCat] = useState([]);       // [{ id, catalogue_image, ... }]

  // ── Pending operations queue ──
  // op: { opId, section, action: 'add'|'delete'|'replace', file, previewUrl, targetId? }
  const [pendingOps, setPendingOps] = useState([]);

  // ── UI state ──
  const [saving, setSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState(null); // { done, total }
  const [showToast, setShowToast] = useState(false);
  const [lightbox, setLightbox] = useState({ open: false, images: [], index: 0 });
  const [deleteModal, setDeleteModal] = useState({ open: false, section: null, itemId: null });

  // ── Queries ──
  const { data: bizList, isSuccess: bizSuccess } = useBusinessQuery.Get_my_bussiness();
  const { mutateAsync: editBusiness } = useBusinessQuery.Edit_business();
  const { data: bizDetail, isSuccess: bizDetailSuccess } = useBusinessQuery.Get_my_bussiness_details(businessId);
  const { data: coverData, isSuccess: coverSuccess } = useImageQuery.Get_cover(businessId);
  const { mutateAsync: addCover } = useImageQuery.Add_cover();
  const { mutateAsync: deleteCover } = useImageQuery.Del_cover();
  const { data: catData, isSuccess: catSuccess } = useImageQuery.Get_Catelogue(businessId);
  const { mutateAsync: addCatalogue } = useImageQuery.Add_catelogue();
  const { mutateAsync: deleteCatalogue } = useImageQuery.Del_catelogue();
  const { mutateAsync: editCatalogue } = useImageQuery.Edit_catelogue();

  // ── Load server data ──
  useEffect(() => {
    if (bizSuccess && bizList?.data?.results?.length > 0) {
      const biz = bizList.data.results[0];
      setBusinessId(biz.id);
      setServerShop(biz.image ? [{ id: "shop_0", url: biz.image }] : []);
      // Seed banner from list; detail fetch below will override with full data
      setServerBanner(biz.banner_image ? [{ id: "banner_0", url: biz.banner_image }] : []);
    }
  }, [bizSuccess, bizList]);

  // ── Override banner/shop from detail endpoint (has full fields) ──
  useEffect(() => {
    if (bizDetailSuccess && bizDetail?.data) {
      const d = bizDetail.data;
      if (d.image !== undefined)
        setServerShop(d.image ? [{ id: "shop_0", url: d.image }] : []);
      if (d.banner_image !== undefined)
        setServerBanner(d.banner_image ? [{ id: "banner_0", url: d.banner_image }] : []);
    }
  }, [bizDetailSuccess, bizDetail]);

  useEffect(() => {
    if (coverSuccess && coverData) {
      setServerCover(coverData.data?.results || []);
    }
  }, [coverSuccess, coverData]);

  useEffect(() => {
    if (catSuccess && catData) {
      setServerCat(catData.data?.results || []);
    }
  }, [catSuccess, catData]);

  // ── Derive display lists from server + pending ops ──
  const deriveList = useCallback((serverItems, section, urlKey) => {
    let list = serverItems.map(item => ({
      id: item.id,
      url: item[urlKey] || item.url,
      pending: false,
    }));

    const ops = pendingOps.filter(op => op.section === section);
    for (const op of ops) {
      if (op.action === "add") {
        list.push({ id: op.opId, url: op.previewUrl, pending: true, opId: op.opId });
      } else if (op.action === "delete") {
        list = list.filter(item => item.id !== op.targetId);
      } else if (op.action === "replace") {
        list = list.map(item =>
          item.id === op.targetId
            ? { ...item, url: op.previewUrl, pending: true, opId: op.opId }
            : item
        );
      }
    }
    return list;
  }, [pendingOps]);

  const shopList    = deriveList(serverShop,   "shop",      "url");
  const bannerList  = deriveList(serverBanner, "banner",    "url");
  const coverList   = deriveList(serverCover,  "cover",     "cover_image");
  const catList     = deriveList(serverCat,    "catalogue", "catalogue_image");

  const hasPending = pendingOps.length > 0;

  // ── File pickers ──
  const pickFiles = (multiple = false) => {
    if (window.FileChannel) {
      return new Promise(resolve => {
        if (multiple) {
          window.fileMultiCallback = files =>
            resolve(Array.isArray(files) ? files.map(parseFlutterFile).filter(Boolean) : []);
          window.FileChannel.postMessage("chooseMultipleFiles");
        } else {
          window.fileCallback = fd => resolve([parseFlutterFile(fd)].filter(Boolean));
          window.FileChannel.postMessage("chooseFile");
        }
      });
    }
    return new Promise(resolve => {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.multiple = multiple;
      input.onchange = e => resolve(Array.from(e.target.files || []));
      input.click();
    });
  };

  // ── Queue helpers ──
  const queueAdd = (section, files, currentCount, limit) => {
    const slots = limit - currentCount;
    if (slots <= 0) return;
    const toAdd = files.slice(0, slots);
    const ops = toAdd.map(file => ({
      opId: newOpId(),
      section,
      action: "add",
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setPendingOps(prev => [...prev, ...ops]);
  };

  const queueDelete = (section, itemId) => {
    // If it's a pending-add, just remove that op
    const existingAdd = pendingOps.find(op => op.section === section && op.action === "add" && op.opId === itemId);
    if (existingAdd) {
      setPendingOps(prev => prev.filter(op => op.opId !== itemId));
      return;
    }
    // If there's already a pending replace for this item, remove that replace first
    setPendingOps(prev => {
      const filtered = prev.filter(op => !(op.section === section && op.targetId === itemId));
      return [...filtered, { opId: newOpId(), section, action: "delete", targetId: itemId }];
    });
  };

  const queueReplace = (section, itemId, file) => {
    // Remove any existing pending op for this item
    setPendingOps(prev => {
      const filtered = prev.filter(op => !(op.section === section && (op.opId === itemId || op.targetId === itemId)));
      return [...filtered, {
        opId: newOpId(),
        section,
        action: "replace",
        targetId: itemId,
        file,
        previewUrl: URL.createObjectURL(file),
      }];
    });
  };

  // ── Add handlers ──
  const handleAdd = async (section, multiple = true) => {
    const currentCount = { shop: shopList, banner: bannerList, cover: coverList, catalogue: catList }[section].length;
    const limit = LIMITS[section];
    if (currentCount >= limit) return;
    const files = await pickFiles(multiple);
    if (!files.length) return;
    queueAdd(section, files, currentCount, limit);
  };

  // ── Replace handler ──
  const handleReplace = async (section, itemId) => {
    const files = await pickFiles(false);
    if (!files.length) return;
    queueReplace(section, itemId, files[0]);
  };

  // ── Delete handler ──
  const handleDeleteRequest = (section, itemId) => {
    setDeleteModal({ open: true, section, itemId });
  };

  const handleDeleteConfirm = confirmed => {
    if (confirmed) {
      queueDelete(deleteModal.section, deleteModal.itemId);
    }
    setDeleteModal({ open: false, section: null, itemId: null });
  };

  // ── Save all pending ──
  const handleSave = async () => {
    if (!businessId) return;
    setSaving(true);
    const ops = [...pendingOps];
    setSaveProgress({ done: 0, total: ops.length });

    try {
      let newShopUrl = serverShop[0]?.url || "";
      let newBannerUrl = serverBanner[0]?.url || "";
      let shopBannerDirty = false;

      for (let i = 0; i < ops.length; i++) {
        const op = ops[i];

        if (op.section === "shop") {
          if (op.action === "add" || op.action === "replace") {
            newShopUrl = await uploadFileToS3(op.file);
            shopBannerDirty = true;
          } else if (op.action === "delete") {
            newShopUrl = "";
            shopBannerDirty = true;
          }
        } else if (op.section === "banner") {
          if (op.action === "add" || op.action === "replace") {
            newBannerUrl = await uploadFileToS3(op.file);
            shopBannerDirty = true;
          } else if (op.action === "delete") {
            newBannerUrl = "";
            shopBannerDirty = true;
          }
        } else if (op.section === "cover") {
          if (op.action === "add") {
            const url = await uploadFileToS3(op.file);
            await addCover({ id: businessId, cover_image: url, is_active: true });
          } else if (op.action === "delete") {
            await deleteCover({ businessId, id: op.targetId });
          } else if (op.action === "replace") {
            const url = await uploadFileToS3(op.file);
            await deleteCover({ businessId, id: op.targetId });
            await addCover({ id: businessId, cover_image: url, is_active: true });
          }
        } else if (op.section === "catalogue") {
          if (op.action === "add") {
            const url = await uploadFileToS3(op.file);
            await addCatalogue({ id: businessId, catalogue_image: url, is_active: true, name: "" });
          } else if (op.action === "delete") {
            await deleteCatalogue({ businessId, id: op.targetId });
          } else if (op.action === "replace") {
            const url = await uploadFileToS3(op.file);
            await editCatalogue({ businessId, id: op.targetId, catalogue_image: url, is_active: true, name: "" });
          }
        }

        setSaveProgress({ done: i + 1, total: ops.length });
      }

      if (shopBannerDirty) {
        const biz = bizList?.data?.results?.[0];
        const payload = { id: businessId, image: newShopUrl, banner_image: newBannerUrl };
        const carry = ["name","mobile_number","category","sub_category","district","post_office","pin_code","state","latitude","longitude","about_us","services_offered","whatsapp_number","facebook","instagram","youtube","website","google_location_link","google_review_link","location_info"];
        carry.forEach(k => { if (biz?.[k]) payload[k] = biz[k]; });
        if (biz?.type) payload["type"] = biz.type.toLowerCase();
        await editBusiness(payload);
        setServerShop(newShopUrl ? [{ id: "shop_0", url: newShopUrl }] : []);
        setServerBanner(newBannerUrl ? [{ id: "banner_0", url: newBannerUrl }] : []);
      }

      setPendingOps([]);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2800);
    } finally {
      setSaving(false);
      setSaveProgress(null);
    }
  };

  // ── Lightbox ──
  const openLightbox = (images, index) => setLightbox({ open: true, images, index });
  const closeLightbox = () => setLightbox({ open: false, images: [], index: 0 });
  const lbPrev = () => setLightbox(l => ({ ...l, index: (l.index - 1 + l.images.length) % l.images.length }));
  const lbNext = () => setLightbox(l => ({ ...l, index: (l.index + 1) % l.images.length }));

  // ── Counter chip ──
  const Counter = ({ list, limit }) => {
    const uploaded = list.length;
    const remaining = limit - uploaded;
    return (
      <div className="ei-counter-chip">
        <span className="ei-counter-uploaded">Uploaded: {uploaded} / {limit}</span>
        <span className="ei-counter-remaining">Remaining: {remaining}</span>
      </div>
    );
  };

  // ── Universal image card action bar (shared by all card types) ──
  // Used by both SingleImageCard and ImageGrid cells
  const CardActions = ({ onEdit, onDelete }) => (
    <div className="ei-card-actions">
      <button className="ei-card-btn ei-card-btn-edit" onClick={onEdit}>
        <EditOutlinedIcon sx={{ fontSize: 13 }} /> Edit
      </button>
      <button className="ei-card-btn ei-card-btn-del" onClick={onDelete}>
        <DeleteOutlineIcon sx={{ fontSize: 13 }} /> Delete
      </button>
    </div>
  );

  // ── Image grid (Cover / Catalogue) ──
  const ImageGrid = ({ section, list, onTapImage }) => (
    <div className="ei-cat-grid">
      {list.map((item, idx) => (
        <div key={item.id} className={`ei-sic ei-sic-grid-cell ${item.pending ? "ei-cell-pending" : ""}`}>
          <div className="ei-sic-img-wrap filled" onClick={() => onTapImage(idx)}>
            <img src={item.url} alt="" />
            {item.pending && <div className="ei-pending-badge">Pending</div>}
          </div>
          <CardActions
            onEdit={() => handleReplace(section, item.id)}
            onDelete={() => handleDeleteRequest(section, item.id)}
          />
        </div>
      ))}
    </div>
  );

  // ── Single-image card (Shop / Banner) ──
  const SingleImageCard = ({ section, list, title, icon, hint, cardClass }) => {
    const item = list[0];
    return (
      <div className={`ei-sic ${cardClass}`}>
        <div className="ei-sic-header">
          {icon}
          <span className="ei-sic-title">{title}</span>
        </div>
        <div
          className={`ei-sic-img-wrap ${item ? "filled" : ""}`}
          onClick={() => !item && handleAdd(section, false)}
        >
          {item ? (
            <>
              <img src={item.url} alt="" onClick={() => openLightbox(list.map(i => i.url), 0)} />
              {item.pending && <div className="ei-pending-badge">Pending</div>}
            </>
          ) : (
            <>
              <AddPhotoAlternateOutlinedIcon className="pg-img-slot-icon" />
              <Typography className="pg-img-slot-hint">{hint}</Typography>
            </>
          )}
        </div>
        <CardActions
          onEdit={() => item ? handleReplace(section, item.id) : handleAdd(section, false)}
          onDelete={() => item && handleDeleteRequest(section, item.id)}
        />
      </div>
    );
  };

  return (
    <div className="pg-root">
      {/* Top bar */}
      <div className="pg-topbar">
        <ArrowBackIcon onClick={() => history.push("/my-profile")} sx={{ color: "#0E3A53", cursor: "pointer", fontSize: 22 }} />
        <Typography className="pg-topbar-title">Edit Images & Catalogues</Typography>
        {hasPending && <span className="ei-pending-dot" title="Unsaved changes" />}
      </div>

      <div className="pg-body">

        {/* ── Info Banner ── */}
        <div className="ei-info-banner">
          <InfoOutlinedIcon className="ei-info-icon" />
          <Typography className="ei-info-text">
            📢 മികച്ച അനുഭവത്തിനായി: വ്യക്തവും ഉയർന്ന നിലവാരമുള്ള ചിത്രങ്ങൾ മാത്രം അപ്ലോഡ് ചെയ്യുക. ഫയൽ വലുപ്പം കഴിയുന്നത്ര കുറവായാൽ നിങ്ങളുടെ ഷോപ്പും കാറ്റലോഗുകളും ഉപഭോക്താക്കൾക്ക് കൂടുതൽ വേഗത്തിൽ ലഭ്യമാകും.
          </Typography>
        </div>

        {/* ── Shop Image + Banner Image — side by side ── */}
        <div className="ei-row-two">
          <SingleImageCard
            section="shop"
            list={shopList}
            title="SHOP IMAGE"
            icon={<ImageOutlinedIcon sx={{ fontSize: 15 }} />}
            hint="Tap to upload"
            cardClass="ei-card-shop"
          />
          <SingleImageCard
            section="banner"
            list={bannerList}
            title="BANNER IMAGE"
            icon={<PanoramaOutlinedIcon sx={{ fontSize: 15 }} />}
            hint="Tap to upload"
            cardClass="ei-card-banner"
          />
        </div>

        {/* ── Cover Image ── */}
        <div className="pg-card ei-card-cover">
          <div className="ei-section-header">
            <Typography className="pg-card-title" sx={{ mb: "0 !important" }}>
              <PhotoLibraryOutlinedIcon /> Cover Image
            </Typography>
            <Counter list={coverList} limit={LIMITS.cover} />
          </div>
          {coverList.length === 0 ? (
            <div className="ei-single-slot" onClick={() => handleAdd("cover", true)}>
              <AddPhotoAlternateOutlinedIcon className="pg-img-slot-icon" />
              <Typography className="pg-img-slot-hint">Tap to upload cover images</Typography>
            </div>
          ) : (
            <ImageGrid
              section="cover"
              list={coverList}
              onTapImage={idx => openLightbox(coverList.map(i => i.url), idx)}
            />
          )}
          {coverList.length < LIMITS.cover && coverList.length > 0 && (
            <Button className="ei-add-cat-btn" onClick={() => handleAdd("cover", true)}>
              <AddIcon sx={{ fontSize: 20 }} /> Add Cover Images
            </Button>
          )}
        </div>

        {/* ── Catalogue Images ── */}
        <div className="pg-card ei-card-catalogue">
          <div className="ei-section-header">
            <Typography className="pg-card-title" sx={{ mb: "0 !important" }}>
              <CollectionsOutlinedIcon /> Catalogue Images
            </Typography>
            <Counter list={catList} limit={LIMITS.catalogue} />
          </div>

          {/* Save progress bar */}
          {saving && saveProgress && (
            <div className="ei-bulk-progress">
              <Typography className="ei-bulk-label">
                Saving {saveProgress.done} of {saveProgress.total}…
              </Typography>
              <LinearProgress
                variant="determinate"
                value={(saveProgress.done / saveProgress.total) * 100}
                sx={{ borderRadius: 4, height: 6, background: "#ddeaf2", "& .MuiLinearProgress-bar": { background: "linear-gradient(90deg,#0E3A53,#2182B1)" } }}
              />
            </div>
          )}

          {catList.length === 0 ? (
            <div className="ei-single-slot" style={{ height: 120 }} onClick={() => handleAdd("catalogue", true)}>
              <AddPhotoAlternateOutlinedIcon className="pg-img-slot-icon" />
              <Typography className="pg-img-slot-hint">Tap to add catalogue images</Typography>
            </div>
          ) : (
            <ImageGrid
              section="catalogue"
              list={catList}
              onTapImage={idx => openLightbox(catList.map(i => i.url), idx)}
            />
          )}

          {catList.length < LIMITS.catalogue && (
            <Button
              className="ei-add-cat-btn"
              onClick={() => handleAdd("catalogue", true)}
              disabled={saving}
            >
              <AddIcon sx={{ fontSize: 20 }} /> Add Catalogue Images
            </Button>
          )}
        </div>

      </div>

      {/* Save bar */}
      <div className="pg-save-bar">
        <Button className="pg-cancel-btn" onClick={() => history.push("/my-profile")}>Cancel</Button>
        <Button
          className="pg-save-btn"
          onClick={handleSave}
          disabled={saving || !hasPending}
          startIcon={saving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : null}
        >
          {saving ? "Saving…" : `Save Changes${hasPending ? ` (${pendingOps.length})` : ""}`}
        </Button>
      </div>

      {/* Toast */}
      {showToast && (
        <div className="pg-toast">
          <CheckCircleIcon sx={{ fontSize: 16 }} /> Saved successfully
        </div>
      )}

      {/* Lightbox */}
      {lightbox.open && (
        <div className="pg-lightbox" onClick={closeLightbox}>
          {lightbox.images.length > 1 && (
            <button className="ei-lb-nav ei-lb-prev" onClick={e => { e.stopPropagation(); lbPrev(); }}>
              <ChevronLeftIcon />
            </button>
          )}
          <img src={lightbox.images[lightbox.index]} alt="preview" onClick={e => e.stopPropagation()} />
          {lightbox.images.length > 1 && (
            <button className="ei-lb-nav ei-lb-next" onClick={e => { e.stopPropagation(); lbNext(); }}>
              <ChevronRightIcon />
            </button>
          )}
          {lightbox.images.length > 1 && (
            <Typography className="ei-lb-counter">
              {lightbox.index + 1} / {lightbox.images.length}
            </Typography>
          )}
          <button className="ei-lb-close" onClick={closeLightbox}><CloseIcon /></button>
        </div>
      )}

      {/* Delete confirm */}
      {deleteModal.open && (
        <ImageRemovalModal open={deleteModal.open} handleClose={handleDeleteConfirm} />
      )}
    </div>
  );
};

function parseFlutterFile(fileData) {
  try {
    if (typeof fileData === "object" && fileData?.fileBase64) {
      const { fileName, fileBase64, fileType } = fileData;
      const b64 = fileBase64.includes("base64,") ? fileBase64.split("base64,")[1] : fileBase64;
      const bytes = atob(b64); const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
      return new File([arr], fileName || `file_${Date.now()}.jpg`, { type: fileType || "image/jpeg" });
    }
    if (typeof fileData === "string" && fileData.includes("base64")) {
      const m = fileData.match(/^data:([^;]+);base64,(.+)$/);
      if (m) {
        const bytes = atob(m[2]); const arr = new Uint8Array(bytes.length);
        for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
        return new File([arr], `file_${Date.now()}.jpg`, { type: m[1] });
      }
    }
  } catch (e) { console.error(e); }
  return null;
}

export default EditImages;
